/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import Loader from '../../common/Loader';
import MessageInfo from '../../common/MessageInfo';
import RangeSlider from '../../common/RangeSlider';
import SportSelection from '../../common/SportSelection';
import BetSelector from '../../common/BetSelector';
import { useFetchFavoriteOdds } from '../../data/fetchFavoriteOdds';
import { byRankingAndTime, enableDisableOdds } from '../../data/parseFavoriteOdds';
import { filterMatchesByTimestamp } from '../../data/utils/filterMatchesByTimestamp';
import { getConfiguration } from '../../data/utils/configuration';
import { getFilterDesc } from '../../data/utils/getFilterDesc';
import { getPrematchAggs } from '../../data/utils/getPrematchAggs';
import { getPrincipaleOPrimoGruppo } from '../../data/utils/getPrincipaleOPrimoGruppo';
import { oddToString } from '../../data/utils/oddToString';
import { labels } from '../../data/labels';
import Match from '../events-by-date/Match';

const STEP = 20;
const ADVANCED_STEP = 50;

function applyFilters(data, sport, oddsFilterMin, oddsFilterMax, timeFilter, betId) {
    let events = data.eventsBySport.get(sport);
    events = filterMatchesByTimestamp(events, timeFilter)

    let key = `${oddsFilterMin}_${oddsFilterMax}`;
    let filtered = [];
    events.forEach(e => {
        if (e.filters.has(betId) && e.filters.get(betId).has(key)) {
            filtered.push(e);
        }
    });

    filtered = filtered.sort(byRankingAndTime);
    enableDisableOdds(filtered, oddsFilterMin, oddsFilterMax);
    return filtered;
}

function getPrintableOdds(odds) {
    return odds.map(o => oddToString(o));
}

function FavoriteOdds(props) {
    const conf = getConfiguration();

    const times = conf.foc.tfs;
    const oddsValuesMin = conf.foc.ods;
    const oddsValues = conf.foc.ods;

    const timeout = useRef(null);
    const [state, setState] = useState({
        data: null,
        events: [],
        oddsFilter: oddsValues[oddsValues.length-1],
        oddsFilterMin: oddsValuesMin[0],
        timeFilter: 'ALL',
        sport: null,
        betId: null,
        handicap: null,
        update(updt) {
            setState(prev => {
                return {...prev, ...updt};
            });
        },
        setData(data) {
            const sport = data.sports[0].id;
            const firstBet = data.eventsBySport.get(sport)[0].gruppiScommessa[0].scommesse[0];
            const betId = `${firstBet.id}_${firstBet.handicap}`;
            const events = applyFilters(data, sport, this.oddsFilterMin, this.oddsFilter, this.timeFilter, betId);
            this.update({data, sport, events, betId});
        },
        changeSport(sportId) {
            clearTimeout(timeout.current);
            const firstBet = this.data.eventsBySport.get(sportId)[0].gruppiScommessa[0].scommesse[0];
            const betId = `${firstBet.id}_${firstBet.handicap}`;
            const events = applyFilters(this.data, sportId, this.oddsFilterMin, this.oddsFilter, this.timeFilter, betId);
            this.update({sport: sportId, events, betId});
            setOffset(STEP);
        },
        changeOddsFilter(index) {
            clearTimeout(timeout.current);
            const oddsFilter = oddsValues[index];
            const events = applyFilters(this.data, this.sport, this.oddsFilterMin, oddsFilter, this.timeFilter, this.betId);
            this.update({events, oddsFilter: oddsFilter});
            setOffset(STEP);
        },
        changeOddsFilterMin(index) {
            clearTimeout(timeout.current);
            const oddsFilterMin = oddsValuesMin[index];
            const events = applyFilters(this.data, this.sport, oddsFilterMin, this.oddsFilter, this.timeFilter, this.betId);
            this.update({events, oddsFilterMin: oddsFilterMin});
            setOffset(STEP);
        },
        changeTimeFilter(index) {
            clearTimeout(timeout.current);
            const timeFilter = times[index];
            const events = applyFilters(this.data, this.sport, this.oddsFilterMin, this.oddsFilter, timeFilter, this.betId);
            this.update({timeFilter, events});
            setOffset(STEP);
        },
        updateBetId(bet) {
            const betId = `${bet.id}_${bet.handicap}`;
            const events = applyFilters(this.data, this.sport, this.oddsFilterMin, this.oddsFilter, this.timeFilter, betId);
            this.update({events, betId});
        }
    });

    function handleOddsOnChange(i) {
        state.changeOddsFilter(i);
    }

    function handleOddsOnChangeMin(i) {
        state.changeOddsFilterMin(i);
    }

    function handleTimeOnChange(i) {
        state.changeTimeFilter(i);
    }

    const [request] = useFetchFavoriteOdds();

    const [showLoader, setShowLoader] = useState(true);
    useEffect(function() {
        let timeout = null;
        if (request.isLoading) {
            timeout = setTimeout(function() {
                setShowLoader(true);
            }, conf.lspt);
        } else {
            clearTimeout(timeout);
            setShowLoader(false);
            if (!request.isError) {
                state.setData(request.data);
            }
        }

        return () => {
            clearInterval(timeout);
        };
    }, [request]);

    const [offset, setOffset] = useState(STEP);
    useEffect(() => {
        if (state.events.length > 0 && offset != null) {
            timeout.current = setTimeout(() => {
                let newOffset = offset;
                if (items.length + offset + ADVANCED_STEP >= state.events.length) {
                    newOffset = null;
                } else {
                    newOffset += ADVANCED_STEP;
                }
                setOffset(newOffset);
            }, 1000);
        }

        return function(){
            clearTimeout(timeout.current);
        }

    }, [state.events, offset]);

    let items = offset ? state.events.slice(0, offset) : state.events;
    const timeRanges = times.map(t => getFilterDesc(t));

    if (showLoader || request.isError) {
        return <Loader error={request.isError} />;
    }

    let date = null;

    if (!showLoader) {
        items = items.map(item => {
            const bets = item.gruppiScommessa[0].scommesse.filter(s => {
                return `${s.id}_${s.handicap}` === state.betId;
            });
            return {...item,gruppiScommessa:[{ scommesse: bets }] }
        })
    }

    let bets;

    if (state.sport) {
        const prematchAggs = Object.values(getPrematchAggs(state.sport));
        const agg = getPrincipaleOPrimoGruppo(prematchAggs);
        bets = agg.scommesse.filter(b => {
            return !b.parametrica && ![15, 16, 17].includes(b.id);
        }).map(b => {
            return {
                ...b,
                uniqueId: `${b.id}_${b.handicap}`
            };
        });
    }

    function changeBet(bet) {
        state.updateBetId(bet);
    }

    let renderBet = bets.find(b =>  `${b.id}_${b.handicap}` === state.betId);

    return (
        <>
            <span id="heading-favorite" className="list-heading">{labels['favorite_odds']}</span>
            <nav id="nav-live-sports" className="nav-slider">
                {state.data.sports.map(sport => {
                    function handleClick(e){
                        e.stopPropagation();
                        e.preventDefault();
                        state.changeSport(sport.id)
                    }
                    return (
                        <SportSelection key={sport.id} {...sport} counter={sport.eventsCount} selected={sport.id === state.sport} handleClick={handleClick} />
                    )
                })}
            </nav>
            <div id="avvenimenti">
                <div className="d-flex flex-column justify-content-center w-100 p-4">
                    <div className="d-flex flex-column align-items-center">
                        <span id="favorite-min">{labels['favorite_odds.min_odd_value']}</span>
                        <RangeSlider elems={getPrintableOdds(oddsValues)} defaultValue={0} onChange={handleOddsOnChangeMin} />
                    </div>
                    <div className="d-flex flex-column align-items-center">
                        <span id="favorite-max">{labels['favorite_odds.max_odd_value']}</span>
                        <RangeSlider elems={getPrintableOdds(oddsValues)} defaultValue={oddsValues.length-1} onChange={handleOddsOnChange} />
                    </div>
                    <div className="d-flex flex-column align-items-center mt-4">
                        <span id="favorite-limits">{labels['favorite_odds.time_limit']}</span>
                        <RangeSlider elems={timeRanges} defaultValue={times.indexOf('ALL')} onChange={handleTimeOnChange} />
                    </div>

                    <div className="d-flex flex-column align-items-center mt-4">
                        <span id="favorite-markets">{labels['favorite_odds.markets']}</span>
                        <BetSelector bets={bets} bet={renderBet} changeBet={changeBet}/>
                    </div>

                </div>
                {items.length > 0 ?
                    <>
                        {items.map(match => {
                            if (match.fullDate.localeCompare(date) !== 0) {
                                date = match.fullDate;
                                return (
                                    <React.Fragment  key={match.uniqueId}>
                                        <span className="heading text-medium">{date}</span>
                                        <Match
                                            match={match}
                                            aggregata={match.openAggregata}
                                            bet={match.gruppiScommessa[0].scommesse[0]}
                                            expanded={false}
                                            showTournamentDesc={true} />
                                    </React.Fragment>
                                );
                            } else {
                                return <Match key={match.uniqueId}
                                            match={match}
                                            aggregata={match.openAggregata}
                                            bet={match.gruppiScommessa[0].scommesse[0]}
                                            expanded={false}
                                            showTournamentDesc={true} />
                            }
                        })}
                    </>
                    :
                    <MessageInfo message={labels['favorite_odds.error.no_events_for_selected_filters']} />
                }
            </div>
        </>
    );
}

export default FavoriteOdds;